import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchTenants, 
  createTenant, 
  updateTenant, 
  deleteTenant,
  selectAllTenants,
  selectTenantLoading,
  selectTenantError 
} from '../store/slices/tenantSlice';

import TenantFilter from './TenantFilter';
import TenantTable from './TenantTable';
import TenantModal from './TenantModal';

const TenantManagement = () => {
  const dispatch = useDispatch();
  const tenants = useSelector(selectAllTenants);
  const loading = useSelector(selectTenantLoading);
  const error = useSelector(selectTenantError);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  
  // Calculate tenant counts
  const counts = {
    all: tenants.length,
    master: tenants.filter(tenant => tenant.is_master).length,
    regular: tenants.filter(tenant => !tenant.is_master).length
  };

  useEffect(() => {
    dispatch(fetchTenants());
  }, [dispatch]);

  const handleOpenModal = (tenant = null) => {
    setCurrentTenant(tenant);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentTenant(null);
  };

  const handleSaveTenant = (tenantData) => {
    if (currentTenant) {
      // Update existing tenant
      dispatch(updateTenant({ 
        id: currentTenant.id, 
        data: tenantData 
      }));
    } else {
      // Create new tenant
      dispatch(createTenant(tenantData));
    }
    handleCloseModal();
  };

  const handleActivate = (id) => {
    dispatch(updateTenant({ id, data: { is_active: true } }));
  };

  const handleDeactivate = (id) => {
    dispatch(updateTenant({ id, data: { is_active: false } }));
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      dispatch(deleteTenant(id));
    }
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px'
  };
  
  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  };
  
  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#111827'
  };
  
  const subtitleStyle = {
    color: '#6b7280'
  };
  
  const buttonStyle = {
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  };
  
  const errorStyle = {
    backgroundColor: '#fee2e2',
    borderLeft: '4px solid #ef4444',
    padding: '16px',
    marginBottom: '24px'
  };
  
  const errorTextStyle = {
    color: '#b91c1c'
  };
  
  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 0'
  };
  
  const spinnerStyle = {
    display: 'inline-block',
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Tenant Management</h1>
          <p style={subtitleStyle}>Manage organizations and their configurations</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          style={buttonStyle}
        >
          <span style={{ marginRight: '8px' }}>+</span> New Tenant
        </button>
      </div>

      {error && (
        <div style={errorStyle}>
          <p style={errorTextStyle}>{error}</p>
        </div>
      )}

      <TenantFilter counts={counts} />

      {loading ? (
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <span style={{ marginLeft: '8px' }}>Loading tenants...</span>
        </div>
      ) : (
        <TenantTable
          onEdit={handleOpenModal}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onDelete={handleDelete}
        />
      )}

      <TenantModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTenant}
        tenant={currentTenant}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TenantManagement;